import { createContext, useContext, useMemo, type MouseEvent, type PropsWithChildren } from 'react'

import type { TFunction } from 'i18next'
import { useTranslation } from 'react-i18next'

import { FluxoPagamentoSheet } from '@/features/simulacao/components/fluxo-pagamento'
import { SimulacaoParcelasAgrupadas } from '@/features/simulacao/components/simulacao-agrupador-parcelas'
import { useBaixarPDFSimulacao, useGetSimulacaoDuplicar } from '@/features/simulacao/hooks'
import { useFluxoPagamento } from '@/features/simulacao/hooks/use-fluxo-pagamento'
import { mapperSimulacaoResponse, useSimulacaoPorId } from '@/features/simulacao/hooks/use-get-simulacao-por-id'
import { useValidarLancarProposta } from '@/features/simulacao/hooks/use-validar-lancar-proposta'
import type {
  TGetSimulacaoDuplicarResponse,
  TGetSimulacaoResponse,
} from '@/features/simulacao/interfaces/simulacao.type'
import { ApiErrorDisplay } from '@/shared/components/api-error-display'
import { Icon } from '@/shared/components/icon'
import { Render } from '@/shared/components/render-if'
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/shared/components/ui'
import { cn } from '@/shared/lib'
import { AlertDialog } from '@/shared/lib/alert-dialog'
import { StringUtils } from '@/shared/utils'
import { formatToBRL } from '@/shared/utils/currency.utils'
import { formatDateToPtBr } from '@/shared/utils/date.utils'

type TGetSimulacaoSuccess = NonNullable<ReturnType<typeof useSimulacaoPorId>['data']>
interface SimulacaoContentContextValue {
  simulacao: TGetSimulacaoSuccess
  fluxoPagamento: ReturnType<typeof useFluxoPagamento>['data']
  t: TFunction<'simulacao', undefined>
  handleIniciarAtendimento?: (clienteId: string) => Promise<void>
}

const SimulacaoContentContext = createContext<SimulacaoContentContextValue | undefined>(undefined)

const useSimulacaoContentContext = () => {
  const context = useContext(SimulacaoContentContext)
  if (!context) {
    throw new Error('useSimulacaoContentContext must be used within SimulacaoContentProvider')
  }
  return context
}

type SimulacaoContentProviderProps =
  | { tipo: 'Cache'; simulacao: TGetSimulacaoResponse; handleIniciarAtendimento?: (clienteId: string) => Promise<void> }
  | { tipo: 'Fetch'; simulacaoId: string; handleIniciarAtendimento?: (clienteId: string) => Promise<void> }

const SimulacaoContentProvider = (props: PropsWithChildren<SimulacaoContentProviderProps>) => {
  const simulacaoId = props.tipo === 'Fetch' ? props.simulacaoId : props.simulacao.id
  const deveRequisitar = props.tipo === 'Fetch'

  const { t } = useTranslation('simulacao')
  const { data: fluxoPagamento } = useFluxoPagamento([simulacaoId])

  const { data: _simulacao, error, refetch, isLoading, isPending } = useSimulacaoPorId(simulacaoId, deveRequisitar)
  const isCarregando = props.tipo === 'Fetch' && (isLoading || isPending)

  const simulacao = (
    props.tipo === 'Cache' ? mapperSimulacaoResponse(props.simulacao) : _simulacao
  ) as TGetSimulacaoSuccess

  const value = useMemo(
    () => ({ simulacao, fluxoPagamento, t, handleIniciarAtendimento: props.handleIniciarAtendimento }),
    [simulacao, fluxoPagamento, t, props.handleIniciarAtendimento],
  )

  if (isCarregando) return <Carregamento />
  if (error) return <ApiErrorDisplay error={error} resetErrorBoundary={refetch} />

  return (
    <SimulacaoContentContext.Provider value={value}>
      <div className="bg-foreground border-muted border p-6">{props.children}</div>
    </SimulacaoContentContext.Provider>
  )
}

type TSimulacaoContentHeaderProps = { onDuplicarSimulacao: (simulacao: TGetSimulacaoDuplicarResponse) => void }
const Header = ({ onDuplicarSimulacao }: TSimulacaoContentHeaderProps) => {
  const { simulacao, t, handleIniciarAtendimento } = useSimulacaoContentContext()

  const { mutateAsync: baixarPdf, isPending: baixandoPdf } = useBaixarPDFSimulacao()
  const { mutateAsync: getSimulacaoDuplicar, isPending: duplicandoSimulacao } = useGetSimulacaoDuplicar()

  const { handleValidarLancarProposta } = useValidarLancarProposta({
    simulacaoId: simulacao.id,
    nomeEmpreendimento: simulacao?.empreendimento.nome,
    tipoDoProduto: simulacao.produto.tipoDoProduto,
    handleIniciarAtendimento: async () => handleIniciarAtendimento?.(simulacao.cliente.id),
  })

  const downloadPdfSimulacao =
    (simulacaoId: string) => async (event: MouseEvent<HTMLDivElement | HTMLButtonElement>) => {
      event.preventDefault()
      await baixarPdf(simulacaoId)
    }

  const confirmarDuplicarSimulacaoDialog = async () =>
    await AlertDialog.show({
      title: t('modais.duplicar_simulacao.titulo'),
      description: t('modais.duplicar_simulacao.descricao'),
      actions: [
        { title: t('modais.duplicar_simulacao.voltar'), value: 'cancel', variant: 'outline' },
        { title: t('modais.duplicar_simulacao.confirmar'), value: 'confirm', variant: 'default' },
      ],
    })

  const handleDuplicarSimulacao = async (simulacaoId: string) => {
    const { value } = await confirmarDuplicarSimulacaoDialog()
    if (value === 'cancel') return

    const simulacaoDuplicada = await getSimulacaoDuplicar(simulacaoId)
    onDuplicarSimulacao(simulacaoDuplicada)
  }

  return (
    <div className="mb-6 flex items-center justify-between">
      <div className="flex flex-col gap-4">
        <h2 className="font-poppins text-base leading-none font-normal tracking-wider uppercase">
          {t('pages.simulacao.simulacao_salva')}
        </h2>
        <div className="font-nunito flex flex-col gap-1 space-x-2 font-medium text-neutral-500 md:flex-row md:gap-0 md:divide-x [&>*]:pr-2 [&>*]:text-sm">
          <span>{t('pages.simulacao.rotinas', { rotinas: simulacao.rotinaArquivo.rotina })}</span>
          <span>{t('pages.simulacao.arquivo', { arquivo: simulacao.rotinaArquivo.arquivo })}</span>
        </div>
      </div>
      <DropdownMenu>
        <DropdownMenuTrigger asChild className="group hidden md:inline-flex">
          <Button className="font-nunito px-2.5">
            <Icon name="chevron-down" className="transition-transform group-data-[state=open]:rotate-180" />
            {t('pages.simulacao.abrir_acoes')}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          sideOffset={2}
          className={cn(
            'font-nunito w-56 p-2 text-sm',
            '[&>[role="menuitem"]]:h-12',
            '[&>[role="menuitem"]]:font-semibold',
            '[&>[role="menuitem"]]:text-sky-700',
            '[&>[role="menuitem"]]:hover:!text-sky-900',
          )}
          align="end"
        >
          <DropdownMenuLabel className="font-nunito text-base font-semibold tracking-wide">
            {t('pages.simulacao.acoes.titulo')}
          </DropdownMenuLabel>
          <DropdownMenuItem onClick={handleValidarLancarProposta}>
            <Icon name="eye-open" className="text-sky-700" />
            {t('pages.simulacao.acoes.lancar_proposta')}
          </DropdownMenuItem>
          <DropdownMenuItem disabled={duplicandoSimulacao} onClick={() => handleDuplicarSimulacao(simulacao.id)}>
            <Icon name="files" className="text-sky-700" />
            {t('pages.simulacao.acoes.duplicar_simulacao')}
          </DropdownMenuItem>
          <DropdownMenuItem disabled={baixandoPdf} onClick={downloadPdfSimulacao(simulacao.id)}>
            <Icon
              name={baixandoPdf ? 'loader' : 'download'}
              className={cn('text-sky-700', { 'animate-spin': baixandoPdf })}
            />
            {baixandoPdf ? t('pages.simulacao.acoes.baixando_pdf') : t('pages.simulacao.acoes.baixar_pdf')}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <div className="fixed right-0 bottom-0 left-0 z-50 inline-flex border-t border-gray-200 bg-white p-4 md:hidden">
        <Sheet>
          <SheetTrigger asChild>
            <Button className="flex w-full items-center gap-2">
              <Icon name="chevron-up" size={16} />
              {t('pages.simulacao.abrir_acoes')}
            </Button>
          </SheetTrigger>

          <SheetContent
            side="bottom"
            className="w-full gap-0 rounded-t-xl border-t border-gray-200 bg-white p-0"
            aria-describedby={undefined}
          >
            <SheetHeader className="p-8" />
            <SheetTitle className="font-poppins px-6 py-4 text-lg leading-none font-normal tracking-normal text-wrap">
              {t('pages.simulacao.breadcrumb.simulacao_salva')}
            </SheetTitle>

            <div className="space-y-3 px-6 pb-8">
              <Button
                variant="ghost"
                onClick={handleValidarLancarProposta}
                className="text-primary font-nunito m-0 flex h-12 w-full items-center justify-start gap-2 font-semibold"
              >
                <Icon name="send" size={16} />
                {t('pages.simulacao.acoes.lancar_proposta')}
              </Button>
              <Button
                variant="ghost"
                className="text-primary font-nunito m-0 flex h-12 w-full items-center justify-start gap-2 text-start font-semibold"
                isLoading={duplicandoSimulacao}
                onClick={() => handleDuplicarSimulacao(simulacao.id)}
              >
                <Icon name="copy" size={16} />
                {t('pages.simulacao.acoes.duplicar_simulacao')}
              </Button>
              <Button
                variant="ghost"
                onClick={downloadPdfSimulacao(simulacao.id)}
                isLoading={baixandoPdf}
                loadingText={t('pages.simulacao.acoes.baixando_pdf')}
                className="text-primary font-nunito m-0 flex h-12 w-full items-center justify-start gap-2 font-semibold"
              >
                <Icon name="import" size={16} />
                {t('pages.simulacao.acoes.baixar_pdf')}
              </Button>
            </div>
          </SheetContent>
        </Sheet>
      </div>
    </div>
  )
}

const EmpreendimentoSection = () => {
  const { simulacao, t } = useSimulacaoContentContext()
  const isLote = simulacao.produto.tipoDoProduto === 'Lote'

  const blocoProdutoMap = {
    Lote: {
      bloco: t('pages.simulacao.empreendimento.quadra'),
      tipoDoProduto: t('pages.simulacao.empreendimento.lote'),
    },
    Unidade: {
      bloco: t('pages.simulacao.empreendimento.torre'),
      tipoDoProduto: t('pages.simulacao.empreendimento.unidade'),
    },
    Acessorio: {
      bloco: t('pages.simulacao.empreendimento.torre'),
      tipoDoProduto: t('pages.simulacao.empreendimento.acessorio'),
    },
  }

  return (
    <div className="mb-0">
      <h3 className="font-poppins flex h-14 items-center bg-neutral-200 px-6 text-base font-normal uppercase">
        {t('pages.simulacao.empreendimento.titulo')}
      </h3>
      <div className="border-border space-y-3 overflow-hidden rounded-none border-r border-l px-6 py-5">
        <div className="border-muted flex flex-col overflow-hidden border-b pb-2">
          <p className="font-poppins mb-2 text-sm font-medium">{t('pages.simulacao.empreendimento.cliente')}</p>
          <p className="font-poppins text-base font-normal">{StringUtils.capitalize(simulacao.cliente.nome)}</p>
        </div>
        <div className="sm:border-border grid grid-cols-12 items-start gap-4 sm:pb-2">
          <div className="border-muted col-span-12 flex flex-col border-b pb-2 sm:col-span-4 sm:border-r sm:border-b-0 sm:pb-0">
            <p className="font-poppins mb-2 text-sm font-medium">
              {t('pages.simulacao.empreendimento.nome_empreendimento')}
            </p>
            <p className="font-poppins text-base font-normal">{simulacao.empreendimento.nome}</p>
          </div>
          <div className="border-muted col-span-12 flex flex-col border-b pb-2 sm:col-span-4 sm:border-r sm:border-b-0 sm:pb-0">
            <p className="font-poppins mb-2 text-sm font-medium">
              {t('pages.simulacao.empreendimento.bloco_produto', {
                bloco: blocoProdutoMap[simulacao.produto.tipoDoProduto].bloco,
                tipoDoProduto: blocoProdutoMap[simulacao.produto.tipoDoProduto].tipoDoProduto,
              })}
            </p>
            <p className="font-poppins text-base font-normal">
              {simulacao?.bloco.nome} - {simulacao?.produto.codigo}
            </p>
          </div>
          <div className="border-muted col-span-12 flex flex-col border-b pb-2 sm:col-span-4 sm:border-none sm:pb-0">
            <p className="font-poppins mb-2 text-sm font-medium">{t('pages.simulacao.empreendimento.metragem')}</p>
            <p className="font-poppins text-base font-normal">
              {t('pages.simulacao.empreendimento.metro_quadrado', {
                metragem: simulacao?.produto.metragemPrivativa,
              })}
            </p>
          </div>
          <div className="bg-muted col-span-12 h-0.25 w-full" />
          <Render.If condition={simulacao.produto.tipoDoProduto === 'Unidade'}>
            {() => (
              <div
                role="grid"
                className="border-muted col-span-12 flex flex-col border-b pb-2 sm:col-span-4 sm:border-r sm:border-b-0 sm:pb-0"
              >
                <p className="font-poppins mb-2 text-sm font-medium">
                  {t('pages.simulacao.empreendimento.marcacao_alvenaria')}
                </p>
                <p className="font-poppins text-base font-normal">
                  {simulacao?.produto.dataMarcacaoAlvenaria
                    ? formatDateToPtBr(simulacao.produto.dataMarcacaoAlvenaria)
                    : '-'}
                </p>
              </div>
            )}
          </Render.If>
          <div className="border-muted col-span-12 flex flex-col border-b pb-2 sm:col-span-4 sm:border-r sm:border-b-0 sm:pb-0">
            <p className="font-poppins mb-2 text-sm font-medium">{t('pages.simulacao.empreendimento.prazo')}</p>
            <p className="font-poppins text-base font-normal">
              {`${simulacao.prazoEmMeses} ${StringUtils.singularPlural(simulacao.prazoEmMeses, 'mês', 'meses')}`}
            </p>
          </div>
          <div
            role="grid"
            className="border-border col-span-12 flex flex-col border-b pb-2 sm:col-span-4 sm:border-none sm:pb-0"
          >
            <p className="font-poppins mb-2 text-sm font-medium">{t('pages.simulacao.empreendimento.data_entrega')}</p>
            <p className="font-poppins text-base font-normal">
              {simulacao?.bloco.previsaoDeEntrega ? formatDateToPtBr(simulacao?.bloco.previsaoDeEntrega) : '-'}
            </p>
          </div>
          <Render.If condition={isLote}>
            {() => (
              <div
                role="grid"
                className="border-border col-span-12 flex flex-col border-b pb-2 sm:col-span-4 sm:border-none sm:pb-0"
              >
                <p className="font-poppins mb-2 text-sm font-medium">
                  {t('pages.simulacao.empreendimento.forma_pagamento')}
                </p>
                <p className="font-poppins text-base font-normal">{simulacao.tipoDeParcelas}</p>
              </div>
            )}
          </Render.If>
        </div>
      </div>
    </div>
  )
}

const ValoresSimuladosSection = () => {
  const { simulacao, fluxoPagamento, t } = useSimulacaoContentContext()

  return (
    <div className="mb-16 sm:mb-6">
      <div className="flex items-center justify-between bg-neutral-200 p-4">
        <h3 className="font-poppins text-base font-normal uppercase">
          {t('pages.simulacao.valores_simulados.titulo')}
        </h3>
        <span className="hidden sm:block">
          <Render.If condition={fluxoPagamento}>
            {(dados) => (
              <FluxoPagamentoSheet titulo={t('fluxo_pagamento', { ns: 'common' })} dados={dados ?? []}>
                <Button variant="outline" className="text-primary h-8 bg-transparent hover:bg-neutral-200">
                  <Icon name="eye-open" className="mr-1 h-4 w-4" />
                  {t('pages.simulacao.valores_simulados.ver_fluxo_pagamento')}
                </Button>
              </FluxoPagamentoSheet>
            )}
          </Render.If>
        </span>
      </div>
      <div className="border-border mb-6 space-y-3 overflow-hidden border-r border-b border-l px-6 py-5">
        <div className="border-muted flex flex-col overflow-hidden border-b pb-2">
          <p className="font-poppins mb-2 text-sm font-medium">
            {t('pages.simulacao.valores_simulados.valor_negociado')}
          </p>
          <p className="font-poppins text-base font-normal">{formatToBRL(simulacao.valorTotalSimulado)}</p>
        </div>
        <div className="sm:gap-b-4 sm:border-muted grid grid-cols-12 items-start gap-4 sm:gap-x-0 sm:gap-y-4 sm:border-b sm:pb-2">
          <div className="border-muted col-span-12 flex flex-col border-b pb-2 sm:col-span-4 sm:border-r sm:border-b-0 sm:pb-0">
            <p className="font-poppins mb-2 text-sm font-medium">{t('pages.simulacao.valores_simulados.poupanca')}</p>
            <p className="font-poppins text-base font-normal">{formatToBRL(simulacao.valorPoupanca)}</p>
          </div>
          <div className="border-muted col-span-12 flex flex-col border-b pb-2 sm:col-span-8 sm:border-r-0 sm:border-b-0 sm:pb-0 sm:pl-4">
            <p className="font-poppins mb-2 text-sm font-medium">{t('pages.simulacao.valores_simulados.saldo')}</p>
            <p className="font-poppins text-base font-normal">{formatToBRL(simulacao.saldo.valor)}</p>
          </div>
        </div>
        <div className="sm:gap-b-4 sm:border-muted grid grid-cols-12 items-start gap-4 sm:gap-x-0 sm:gap-y-4 sm:border-b sm:pb-2">
          <div className="border-muted col-span-12 flex flex-col border-b pb-2 sm:col-span-4 sm:border-r sm:border-b-0 sm:pb-0">
            <p className="font-poppins mb-2 text-sm font-medium">{t('pages.simulacao.valores_simulados.entrada')}</p>
            <Render.If
              condition={simulacao.entrada}
              fallback={
                <p className="font-poppins text-base font-normal uppercase">
                  {t('pages.simulacao.valores_simulados.nenhuma_parcela')}
                </p>
              }
            >
              {(entradas) => <SimulacaoParcelasAgrupadas parcelas={entradas} isGrupoSaldo={false} />}
            </Render.If>
          </div>
          <div className="border-border col-span-12 flex flex-col border-b pb-2 sm:col-span-8 sm:border-r-0 sm:border-b-0 sm:pb-0 sm:pl-4">
            <p className="font-poppins mb-2 text-sm font-medium">{t('pages.simulacao.valores_simulados.mensais')}</p>
            <Render.If
              condition={simulacao.mensais}
              fallback={
                <p className="font-poppins text-base font-normal uppercase">
                  {t('pages.simulacao.valores_simulados.nenhuma_parcela')}
                </p>
              }
            >
              {(mensais) => <SimulacaoParcelasAgrupadas parcelas={mensais} isGrupoSaldo={false} />}
            </Render.If>
          </div>
        </div>
        <div className="sm:gap-b-4 sm:border-muted grid grid-cols-12 items-start gap-4 sm:gap-x-0 sm:gap-y-4 sm:pb-2">
          <div className="border-muted col-span-12 flex flex-col border-b pb-2 sm:col-span-4 sm:border-r sm:border-b-0 sm:pb-0">
            <p className="font-poppins mb-2 text-sm font-medium">
              {t('pages.simulacao.valores_simulados.intermediarias')}
            </p>
            <Render.If
              condition={simulacao.intermediarias}
              fallback={
                <p className="font-poppins text-base font-normal uppercase">
                  {t('pages.simulacao.valores_simulados.nenhuma_parcela')}
                </p>
              }
            >
              {(intermediarias) => <SimulacaoParcelasAgrupadas parcelas={intermediarias} isGrupoSaldo={false} />}
            </Render.If>
          </div>
          <div className="border-border col-span-12 flex flex-col pb-2 sm:col-span-8 sm:border-r-0 sm:border-b-0 sm:pb-0 sm:pl-4">
            <p className="font-poppins mb-2 text-sm font-medium">
              {t('pages.simulacao.valores_simulados.pos_conclusao')}
            </p>
            <Render.If
              condition={simulacao.posConclusao}
              fallback={
                <p className="font-poppins text-base font-normal uppercase">
                  {t('pages.simulacao.valores_simulados.nenhuma_parcela')}
                </p>
              }
            >
              {(posConclusao) => <SimulacaoParcelasAgrupadas parcelas={posConclusao} isGrupoSaldo={false} />}
            </Render.If>
          </div>
        </div>
        <span className="block sm:hidden">
          <Render.If condition={fluxoPagamento}>
            {(dados) => (
              <FluxoPagamentoSheet titulo={t('fluxo_pagamento', { ns: 'common' })} dados={dados ?? []}>
                <Button variant="outline" className="text-primary h-12 w-full bg-transparent hover:bg-neutral-200">
                  <Icon name="eye-open" className="mr-1 h-4 w-4" />
                  {t('pages.simulacao.valores_simulados.ver_fluxo_pagamento')}
                </Button>
              </FluxoPagamentoSheet>
            )}
          </Render.If>
        </span>
      </div>
    </div>
  )
}

const Carregamento = () => {
  return (
    <div className="bg-foreground border-muted border p-6" data-testid="skeleton-simulacao-content">
      <div className="mb-6 flex items-center justify-between">
        <div className="flex flex-col gap-4">
          <div className="h-5 w-48 animate-pulse rounded bg-neutral-200" />
          <div className="flex flex-col gap-1 space-x-2 md:flex-row md:gap-0 md:divide-x [&>*]:pr-2">
            <div className="h-4 w-24 animate-pulse rounded bg-neutral-200" />
            <div className="h-4 w-32 animate-pulse rounded bg-neutral-200" />
          </div>
        </div>
        <div className="hidden md:block">
          <div className="h-10 w-32 animate-pulse rounded bg-neutral-200" />
        </div>
      </div>
      <div className="mb-0">
        <div className="flex h-14 items-center bg-neutral-200 px-6">
          <div className="h-4 w-40 animate-pulse rounded bg-neutral-300" />
        </div>
        <div className="border-border space-y-4 overflow-hidden rounded-none border-r border-l px-6 py-5">
          <div className="border-muted flex flex-col overflow-hidden border-b pb-4">
            <div className="mb-2 h-4 w-16 animate-pulse rounded bg-neutral-200" />
            <div className="h-5 w-48 animate-pulse rounded bg-neutral-200" />
          </div>
          <div className="sm:border-border grid grid-cols-12 items-start gap-4 sm:pb-2">
            <div className="border-muted col-span-12 flex flex-col border-b pb-4 sm:col-span-4 sm:border-r sm:border-b-0 sm:pb-0">
              <div className="mb-2 h-4 w-32 animate-pulse rounded bg-neutral-200" />
              <div className="h-5 w-40 animate-pulse rounded bg-neutral-200" />
            </div>
            <div className="border-muted col-span-12 flex flex-col border-b pb-4 sm:col-span-4 sm:border-r sm:border-b-0 sm:pb-0">
              <div className="mb-2 h-4 w-28 animate-pulse rounded bg-neutral-200" />
              <div className="h-5 w-36 animate-pulse rounded bg-neutral-200" />
            </div>
            <div className="border-muted col-span-12 flex flex-col border-b pb-4 sm:col-span-4 sm:border-none sm:pb-0">
              <div className="mb-2 h-4 w-20 animate-pulse rounded bg-neutral-200" />
              <div className="h-5 w-24 animate-pulse rounded bg-neutral-200" />
            </div>
          </div>
          <div className="border-muted grid grid-cols-12 items-start gap-4 border-t pt-4 sm:pb-2">
            <div className="border-muted col-span-12 flex flex-col border-b pb-4 sm:col-span-4 sm:border-r sm:border-b-0 sm:pb-0">
              <div className="mb-2 h-4 w-32 animate-pulse rounded bg-neutral-200" />
              <div className="h-5 w-40 animate-pulse rounded bg-neutral-200" />
            </div>
            <div className="border-muted col-span-12 flex flex-col border-b pb-2 sm:col-span-4 sm:border-r sm:border-b-0 sm:pb-0">
              <div className="mb-2 h-4 w-28 animate-pulse rounded bg-neutral-200" />
              <div className="h-5 w-36 animate-pulse rounded bg-neutral-200" />
            </div>
            <div className="border-muted col-span-12 flex flex-col border-b pb-2 sm:col-span-4 sm:border-none sm:pb-0">
              <div className="mb-2 h-4 w-20 animate-pulse rounded bg-neutral-200" />
              <div className="h-5 w-24 animate-pulse rounded bg-neutral-200" />
            </div>
          </div>
        </div>
      </div>
      <div className="mb-16 sm:mb-6">
        <div className="flex items-center justify-between bg-neutral-200 p-4">
          <div className="h-4 w-36 animate-pulse rounded bg-neutral-300" />
          <div className="hidden sm:block">
            <div className="h-8 w-40 animate-pulse rounded bg-neutral-300" />
          </div>
        </div>
        <div className="border-border mb-6 space-y-3 overflow-hidden border-r border-b border-l px-6 py-5">
          <div className="border-muted flex flex-col overflow-hidden border-b pb-2">
            <div className="mb-2 h-4 w-32 animate-pulse rounded bg-neutral-200" />
            <div className="h-5 w-40 animate-pulse rounded bg-neutral-200" />
          </div>
          <div className="sm:gap-b-4 sm:border-muted grid grid-cols-12 items-start gap-4 sm:gap-x-0 sm:gap-y-4 sm:border-b sm:pb-2">
            <div className="border-muted col-span-12 flex flex-col border-b pb-2 sm:col-span-4 sm:border-r sm:border-b-0 sm:pb-0">
              <div className="mb-2 h-4 w-20 animate-pulse rounded bg-neutral-200" />
              <div className="h-5 w-32 animate-pulse rounded bg-neutral-200" />
            </div>
            <div className="border-muted col-span-12 flex flex-col border-b pb-2 sm:col-span-8 sm:border-r-0 sm:border-b-0 sm:pb-0 sm:pl-4">
              <div className="mb-2 h-4 w-16 animate-pulse rounded bg-neutral-200" />
              <div className="h-5 w-36 animate-pulse rounded bg-neutral-200" />
            </div>
          </div>
          <div className="sm:gap-b-4 sm:border-muted grid grid-cols-12 items-start gap-4 sm:gap-x-0 sm:gap-y-4 sm:border-b sm:pb-2">
            <div className="border-muted col-span-12 flex flex-col border-b pb-2 sm:col-span-4 sm:border-r sm:border-b-0 sm:pb-0">
              <div className="mb-2 h-4 w-16 animate-pulse rounded bg-neutral-200" />
              <div className="h-5 w-28 animate-pulse rounded bg-neutral-200" />
            </div>
            <div className="border-muted col-span-12 flex flex-col border-b pb-2 sm:col-span-8 sm:border-r-0 sm:border-b-0 sm:pb-0 sm:pl-4">
              <div className="mb-2 h-4 w-20 animate-pulse rounded bg-neutral-200" />
              <div className="h-5 w-32 animate-pulse rounded bg-neutral-200" />
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export const SimulacaoContent = {
  Provider: SimulacaoContentProvider,
  Header,
  EmpreendimentoSection,
  ValoresSimuladosSection,
}
